/**
 * Customized Mini Pet Service
 * 用户在 Magic Lab 生成的定制化 Mini Pet 相关服务
 */

import supabase from '@/lib/supabase';
import { CustomizedMiniPet, CanvasAnimationData } from '../types';

/**
 * 获取用户所有的 Customized Mini Pets（用于选择界面）
 */
export const getAllCustomizedMinipets = async (userId: string): Promise<CustomizedMiniPet[]> => {
  const { data, error } = await supabase
    .from('customized_mini_pet')
    .select('*')
    .eq('user_id', userId)
    .eq('archived', false)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

/**
 * 获取用户选中的 Customized Mini Pet
 */
export const getSelectedCustomizedMinipet = async (userId: string): Promise<CustomizedMiniPet | null> => {
  // 先获取 profile 中的 selected_customized_minipet_id
  const { data: profile } = await supabase
    .from('profiles')
    .select('selected_customized_minipet_id')
    .eq('id', userId)
    .single();

  if (!profile?.selected_customized_minipet_id) {
    return null;
  }

  // 再查询具体的 minipet 数据
  const { data } = await supabase
    .from('customized_mini_pet')
    .select('*')
    .eq('id', profile.selected_customized_minipet_id)
    .maybeSingle();

  return data;
};

/**
 * 从数据库加载 Canvas 动画数据
 * 注意：现在从 customized_mini_pet 表读取，不再依赖 R2 的 metadata.json
 */
export const loadCustomizedMinipetMetadata = async (sessionId: string): Promise<CanvasAnimationData> => {
  // 从数据库查询
  const { data: pet, error } = await supabase
    .from('customized_mini_pet')
    .select('frame_urls, bundle_url, audio_url')
    .eq('session_id', sessionId)
    .single();

  if (error || !pet) {
    throw new Error(`Failed to load minipet metadata: ${error?.message || 'Not found'}`);
  }

  // 从 frame_urls 提取帧信息
  const frameUrls = pet.frame_urls || [];

  return {
    sessionId,
    frames: frameUrls,
    bundleUrl: pet.bundle_url || null,  // 新增：优先使用 bundle
    fps: 24, // 默认值，实际播放时可以调整
    width: 360, // 默认值
    height: 360, // 默认值
    totalFrames: frameUrls.length,
    audioUrl: pet.audio_url || null,
    hasAudio: !!pet.audio_url
  };
};

/**
 * 设置用户选中的 Minipet
 */
export const selectCustomizedMinipet = async (userId: string, petId: number | null): Promise<void> => {
  const { error } = await supabase
    .from('profiles')
    .update({ selected_customized_minipet_id: petId })
    .eq('id', userId);

  if (error) throw error;
};

/**
 * 删除 Customized Mini Pet
 */
export const deleteCustomizedMinipet = async (petId: number): Promise<void> => {
  const { error } = await supabase
    .from('customized_mini_pet')
    .delete()
    .eq('id', petId);

  if (error) throw error;
};
